import { CircleDollarSign } from 'lucide-react';

import { Drawer, DrawerContent, DrawerTitle } from '@/components/ui/drawer';
import { NavSections } from '@/components/ui/side-menu/nav-sections';
import { useNavAccordion } from '@/components/ui/side-menu/useNavAccordion';
import { SIDE_MENU_DATA } from '@/components/ui/side-menu/side-menu-data';
import { TreeSelect as TreeSelectUI } from '@/components/ui/tree-select';
import { useEntityTreeNodes } from '@/hooks/useEntityTreeNodes';
import { usePrepaySaveGate } from '@/hooks/usePrepaySaveGate';

export interface MobileNavDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isLogin: boolean;
  setIsLoading?: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * The <1024px navigation: a bottom sheet holding the entity search, the
 * full nav accordion (shared with the desktop sidebar via NavSections),
 * Prepay & Save, and the bottom utility section. Opens from MobileHeader's
 * hamburger; closes on nav selection, drag-down, or overlay tap.
 */
const MobileNavDrawer = ({
  open,
  onOpenChange,
  isLogin,
  setIsLoading
}: MobileNavDrawerProps) => {
  const treeNodes = useEntityTreeNodes();
  const showPrepayLink = usePrepaySaveGate(isLogin);
  // One shared accordion owner for both NavSections instances below — see
  // useNavAccordion's doc comment for why this can't be owned per-instance.
  const { openSections, toggleSection } = useNavAccordion();
  const mainItems = SIDE_MENU_DATA.filter((c) => !c.bottom);
  const bottomItems = SIDE_MENU_DATA.filter((c) => c.bottom);
  const close = () => onOpenChange(false);

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[85dvh]" aria-describedby={undefined}>
        <DrawerTitle className="sr-only">Navigation</DrawerTitle>
        <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-6 pt-2">
          <div className="pb-3">
            <TreeSelectUI
              nodes={treeNodes}
              md="9"
              controlId="mobileNavTreeSelect"
              type="text"
              ariaDescribedBy="entityHelpBlock"
              placeholder="Search Entity..."
              name="entityTree"
              layout="block"
              setIsLoading={setIsLoading}
              onSwitched={close}
            />
          </div>
          <nav aria-label="Main">
            <NavSections
              sections={mainItems}
              variant="drawer"
              openSections={openSections}
              onToggleSection={toggleSection}
              onNavigate={close}
            />
          </nav>
          {/* Ported from the desktop header verbatim, including the
              stroke="white" knockout on the icon glyph. Hidden for
              Emma-category accounts, same as the desktop header (DV-2677). */}
          {showPrepayLink && (
            <a
              href="https://www.planomatic.com/prepay-save"
              target="_blank"
              rel="noopener noreferrer"
              className="flex min-h-11 items-center gap-3 px-3 py-2 text-base font-medium text-foreground no-underline"
            >
              <CircleDollarSign
                className="h-6 w-6"
                fill="currentColor"
                stroke="white"
                strokeWidth={1.5}
              />
              Prepay &amp; Save 25%
            </a>
          )}
          {bottomItems.length > 0 && (
            <div className="mt-2 border-t border-border pt-2">
              <NavSections
                sections={bottomItems}
                variant="drawer"
                openSections={openSections}
                onToggleSection={toggleSection}
                onNavigate={close}
              />
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default MobileNavDrawer;
